import React from 'react';
import { ArrowRightIcon, FilmIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { clientGreeting } from '../../utils/time';
import { projectStatus, showClientHeadline, showStatus } from '../../utils/status';
import { PosterTile } from '../../components/DeliverableGroups';

/**
 * The landing page for a show link — every project Portal has made for this
 * client under this show, in the same catalog-style numbered list used
 * elsewhere in the client portal. Opening one enters the ordinary
 * single-project client experience unchanged.
 *
 * Header follows the same pattern as the per-project Home.tsx one level
 * down: a time-of-day greeting, the CLIENT's name as the dominant heading,
 * then the show name as the subtitle beneath it — so a show link reads the
 * same way a project link always has, just with an episode list in place of
 * a single project's deliverables.
 *
 * Only ever reached once a show has more than one episode — a one-episode
 * show is routed straight into that episode instead (see the show-link
 * bootstrap in PortalContext), so this page never has to represent a list
 * of exactly one thing.
 */
export function ShowHome() {
  const {
    activeShow,
    showProjects,
    clientName,
    goClient,
    openShowDeliverable,
    openShowFiles
  } = usePortal();
  const headline = showClientHeadline(showProjects);
  /* Same test `showClientHeadline` uses internally to decide its own
     "Your show is complete" message — recomputed here so the banner can
     offer a "View Files" action once nothing is outstanding, the way a
     single project's own Home page already does via `projectComplete`. */
  const complete = showProjects.length > 0 && showStatus(showProjects) === 'Complete';

  /* When more than one deliverable across the show is awaiting review,
     `headline.actionId` only ever names the first of them — jumping straight
     into that one episode would leave a client wondering what the other
     ones were (raised 10 Sep 2026, same fix as the per-project Home page).
     The pooled Deliverables tab lists every awaiting item across every
     episode, so that is where this sends the client instead of guessing. */
  function startReview() {
    if (headline.multiple) {
      goClient('show-deliverables');
      return;
    }
    if (headline.actionId && headline.actionProjectId) {
      openShowDeliverable(headline.actionProjectId, headline.actionId);
    }
  }
  return <div data-id="element-2168" data-mp="3m8qg68k3ofg9">
      <section data-id="element-2169" data-mp="3m8gpeir8e49i">
        <p className="label text-mute" data-id="element-2170" data-mp="3i5aolqmw15af">{clientGreeting(clientName)}</p>
        <h1 className="mt-6 font-display text-6xl leading-[0.9] tracking-tight sm:text-8xl" data-id="element-2171" data-mp="3i7576yaoslbc">
          {(activeShow?.client ?? '').toUpperCase()}
        </h1>
        <p className="mt-8 font-display text-2xl text-charcoal sm:text-3xl" data-id="element-2172" data-mp="38e6asu8y7qh6">
          {(activeShow?.name ?? '').toUpperCase()}
        </p>
        <p className="label mt-5 max-w-2xl text-mute" data-id="element-2173" data-mp="9rbt0q3tvlxg">
          Everything Portal has made for you under this show. Open an episode to review its
          deliverables, or see everything awaiting review at once in Deliverables above.
        </p>
      </section>

      {/* Same status card as a single project's own Home page, one level up
          — pooled across every episode of the show, so what's actually
          being asked of the client is the very first thing they see,
          rather than something they'd only find by opening the right
          episode first. */}
      {showProjects.length > 0 && <section className="mt-10 bg-ink px-7 py-12 text-paper sm:mt-14 sm:px-12 sm:py-16" data-id="element-2174" data-mp="107m680u5jeov">
          <p className="max-w-3xl font-display text-3xl leading-tight sm:text-5xl" data-id="element-2175" data-mp="14ycd5ii87plo">
            {headline.message}
          </p>
          <p className="mt-6 max-w-2xl font-display text-lg text-paper/60 sm:text-xl" data-id="element-2176" data-mp="19xoytzxd2qmo">
            {headline.support}
          </p>

          {headline.actionId ? <div className="mt-10" data-id="element-2177" data-mp="1iy3mh5xsshtc">
              <button type="button" onClick={startReview} className="label group flex items-center gap-3 border border-paper px-7 py-4 transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-2178" data-mp="1ixbzukm6huf7">
                {headline.actionLabel}
                <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-2179" data-mp="1ezv6qnsrudkw" />
              </button>
            </div> : (
      /* A completed show has nothing left to review, but a client
         should still be one click from its files — the same "View
         Files" action a completed single project already offers on
         its own Home page. There is no single show-wide Files page,
         so this opens the last episode's own one (see
         `openShowFiles` in PortalContext). */
      complete && <div className="mt-10" data-id="element-2180" data-mp="22ppit359ny43">
                <button type="button" onClick={() => openShowFiles(showProjects[showProjects.length - 1].id)} className="label group flex items-center gap-3 border border-paper px-7 py-4 transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-2181" data-mp="22sv8oklpz40v">
                  View Files
                  <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-2182" data-mp="3gntv8ob04x5x" />
                </button>
              </div>)}
        </section>}

      <ul className="m-0 mt-16 list-none border-t border-rule p-0" data-id="element-2183" data-mp="3ulifpv15q2ag">
        {showProjects.map((project, index) => <li key={project.id} className="border-b border-rule" data-id="element-2184" data-mp="3epg2vm0ib81" data-mpi={index}>
            <ShowProjectRow index={index} name={project.name} status={projectStatus(project)} projectId={project.id} data-id="element-2185" data-mp="r5bbnnety315" />
          </li>)}
        {showProjects.length === 0 && <li className="border-b border-rule py-8 font-display text-xl text-mute" data-id="element-2186" data-mp="15qh4mqd89jiy">
            Nothing here yet.
          </li>}
      </ul>
    </div>;
}
function ShowProjectRow({
  index,
  name,
  status,
  projectId
}: {
  index: number;
  name: string;
  /** A short status word — e.g. "Awaiting Client Review", "Filming",
      "Complete" — in place of a plain deliverable count, so the episode
      most needing attention is obvious at a glance even without opening
      it. Same wording `projectStatus` already shows on a single project's
      own Home page, one level up. */
  status: string;
  projectId: string;
}) {
  const {
    openShowProject
  } = usePortal();
  return <button type="button" onClick={() => openShowProject(projectId)} className="group grid w-full grid-cols-1 items-center gap-x-6 gap-y-3 py-8 text-left sm:grid-cols-12" data-id="element-2187" data-mp="86mgtqay3vi2">
      {/* Same ink-and-light poster-frame mark the Deliverables list one
          level down already uses per row, so opening an episode isn't a
          step up in visual richness — a generic film mark, since an
          episode isn't any one deliverable type. */}
      <span className="sm:col-span-1" data-id="element-2188" data-mp="2jrv9389da5ye">
        <PosterTile data-id="element-2189" data-mp="2jq23zxeejtsi">
          <FilmIcon className="relative h-3.5 w-3.5 text-paper/70" strokeWidth={1.25} data-id="element-2190" data-mp="2or7x3wbz5j2k" />
        </PosterTile>
      </span>
      <span className="label text-mute sm:col-span-1" data-id="element-2191" data-mp="1r3zh2guqt27y">{String(index + 1).padStart(2, '0')}</span>
      <span className="font-display text-3xl leading-none sm:col-span-7 sm:text-4xl" data-id="element-2192" data-mp="1w21051s651jf">{name}</span>
      <span className="label flex items-center gap-4 text-ink sm:col-span-3 sm:justify-self-end" data-id="element-2193" data-mp="21159bsf3ez9r">
        {status}
        <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 opacity-40 transition-transform duration-150 ease-out group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.25} data-id="element-2194" data-mp="25sndepcexfyy" />
      </span>
    </button>;
}